import type { CommentElementProps, DialogRef, DynamicFieldComponentProps, OdinKey } from '@myosh/odin-components';
import { Button, OdinIcon } from '@myosh/odin-components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { skipWhile, Subject, take, takeUntil } from 'rxjs';
import type { AiFieldConfigProps, CombinedFieldType } from '../../../@types/forms';
import type { RecordResult } from '../../../@types/records';
import {
  useGetGenerateRecordFieldValuesJobDetailsQuery,
  usePostGenerateFieldsValuesJobMutation,
} from '../../../redux/services/ai-api';
import { useFormSettingsQuery } from '../../../redux/services/api';
import { extractErrorMessage } from '../../../utils/error-utils';
import ConfirmationDialog from '../../common/confirmation-dialog.component';
import { RecordSaveState } from '../../record/record.component';

interface OptionValue {
  id: OdinKey;
  caption: string;
}
interface AiOutputField {
  id: OdinKey;
  caption: string;
  fieldName: string;
  type: CombinedFieldType;
}

interface AIFieldProps extends DynamicFieldComponentProps<void>, AiFieldConfigProps {
  recordId: number;
  buttonCaption: string;
  showFieldCaption: boolean;
}

const AIField = ({
  id,
  recordId,
  buttonCaption,
  readOnly,
  formId,
  aiOutputFieldIds,
  recordSaveState,
  setFieldValue,
  formIsDirty,
  saveRecord,
  getRecordData,
}: AIFieldProps) => {
  const [errorMessage, setErrorMessage] = useState<unknown>();
  const [jobId, setJobId] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  const destroySubject = useRef(new Subject<void>());
  const confirmationModalRef = useRef<DialogRef>(null);
  const { t } = useTranslation();

  const [postGenerateFieldsValuesJob] = usePostGenerateFieldsValuesJobMutation();

  const { data, isFetching } = useGetGenerateRecordFieldValuesJobDetailsQuery(jobId!, {
    skip: !jobId,
    pollingInterval: 6000,
  });

  const { data: formSettings } = useFormSettingsQuery(formId ?? 0, {
    skip: formId === undefined || formId === 0,
  });

  const aiOutputFields: Record<string, AiOutputField> = useMemo(() => {
    const safeAiOutputFieldIds = Array.isArray(aiOutputFieldIds) ? aiOutputFieldIds : [];
    return (
      formSettings?.fields
        ?.flatMap((field) => field.fields || [])
        .filter((field) => safeAiOutputFieldIds.includes(Number(field.id)))
        .reduce(
          (acc, field) => {
            acc[field.caption] = {
              id: field.id,
              caption: field.caption,
              fieldName: field.fieldName,
              type: field.fieldType as CombinedFieldType,
            };
            return acc;
          },
          {} as Record<string, AiOutputField>
        ) ?? {}
    );
  }, [formSettings?.fields, aiOutputFieldIds]);

  useEffect(() => {
    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  useEffect(() => {
    if (jobId && !isFetching && data) {
      if (data.status === 'FINISHED') {
        setJobId(undefined);
        setLoading(false);

        if (data.result && data.result.length > 0) {
          data.result.forEach((generatedOutputValue) => {
            const aiOutputField = aiOutputFields[String(generatedOutputValue.key)];
            if (!aiOutputField) return;

            const { type } = aiOutputField;
            const generatedValue = generatedOutputValue.value;

            switch (type) {
              case 'HISTORY':
                handleHistoryField(aiOutputField.fieldName, generatedValue);
                break;
              case 'RECORD_OPTIONGROUP':
                handleOptionBasedField(aiOutputField.fieldName, generatedValue, false);
                break;
              case 'RECORD_MULTISELECTCHECKBOX':
                handleOptionBasedField(aiOutputField.fieldName, generatedValue, true);
                break;
              default:
                handleGenericField(aiOutputField.fieldName, generatedValue);
                break;
            }
          });
        }
      } else if (data.status === 'FAILED') {
        setJobId(undefined);
        setLoading(false);
        setErrorMessage(data?.error ?? t('ai-field-generation-error'));
      }
    }
  }, [jobId, isFetching, data, aiOutputFields]);

  const onConfirm = () => {
    saveRecord?.()
      ?.pipe(
        skipWhile((result) => !result || Object.keys(result).length === 0),
        take(1),
        takeUntil(destroySubject.current)
      )
      .subscribe((record: RecordResult) => {
        invokeAIButton(Number(record.id));
      });
  };

  const handleClick = useCallback(() => {
    if (recordSaveState === RecordSaveState.New || formIsDirty?.()) {
      confirmationModalRef.current?.show();
    } else {
      invokeAIButton(recordId);
    }
  }, [recordId, formIsDirty, saveRecord]);

  const invokeAIButton = useCallback(
    (recordId: number) => {
      if (Object.keys(aiOutputFields).length > 0) {
        setLoading(true);
        setErrorMessage(undefined);

        postGenerateFieldsValuesJob({
          aiRecordFieldsGenerationRequestDto: {
            aiFieldId: Number(id),
            moduleFormId: formId,
            recordId: recordId,
          },
        })
          .unwrap()
          .then(({ backgroundJobId }) => setJobId(backgroundJobId))
          .catch((error) => {
            setLoading(false);
            setErrorMessage(error);
          });
      }
    },
    [aiOutputFields, formId, id, setFieldValue]
  );

  const handleHistoryField = useCallback(
    (fieldName: string, generatedValue: string) => {
      const existingValue = (getRecordData()?.[fieldName] as CommentElementProps[]) ?? [];
      setFieldValue(fieldName, [...existingValue, { message: generatedValue, draft: true }]);
    },
    [getRecordData, setFieldValue]
  );

  const handleOptionBasedField = useCallback(
    (fieldName: string, generatedValue: string, isMultiSelect: boolean = false) => {
      if (!generatedValue) return;
      const values = safeParseValue(generatedValue);

      if (isMultiSelect && Array.isArray(values)) {
        if (values.length === 0) {
          setFieldValue(fieldName, []);
          return;
        }
        const transformedValues = values.map((value) => ({
          value: value.id,
          text: value.caption,
        }));

        setFieldValue(fieldName, transformedValues);
        return;
      }

      // Single select case
      const value = values as OptionValue;
      if (value) {
        setFieldValue(fieldName, {
          text: value.caption,
          value: value.id,
        });
      } else {
        setFieldValue(fieldName, {});
      }
    },
    [setFieldValue]
  );

  const handleGenericField = useCallback(
    (fieldName: string, generatedValue: string) => {
      setFieldValue(fieldName, generatedValue);
    },
    [setFieldValue]
  );

  return (
    <>
      {!readOnly && (
        <>
          <div className="my-2 ml-1 flex w-fit flex-col">
            <Button type="primary" onClick={handleClick} disabled={loading}>
              <div className="flex items-center gap-2">
                {buttonCaption}
                {loading && <OdinIcon icon="Loader" spin={true} />}
              </div>
            </Button>
          </div>
          {errorMessage && (
            <p className="px-1 text-sm text-info">
              {extractErrorMessage(errorMessage, t('ai-field-generation-error'))}
            </p>
          )}
          <ConfirmationDialog ref={confirmationModalRef} onConfirm={onConfirm} hasCancelButton />
        </>
      )}
    </>
  );
};

export default React.memo(AIField);

function safeParseValue(value: string): OptionValue[] | OptionValue | string | null {
  if (typeof value !== 'string') return value;

  const firstChar = value.trim()[0];
  if (firstChar !== '[' && firstChar !== '{') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
